import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import * as cookie from 'react-cookies';
import API from '../../../services/API';
import {
  composeValidators,
  email as emailValidator,
  required,
} from '../../../utilities/validators';
import { InputStyled } from '../../_helpers/form/Input';
import { ButtonStyled } from '../../_helpers/form/Button';
import { CheckboxStyled } from '../../_helpers/form/Checkbox';
import { ErrorPopup } from '../../_helpers/form/FormStyles';
import { Loader } from '../../_helpers/Loader/Loader';
import { UPSELL_NEWSLETTER_APPLIED_COOKIE } from '../../UpsellNewsletter/UpsellNewsletter';

const Wrapper = styled.div`
  background: rgb(211, 229, 247);
  position: relative;
  padding: ${rem(28)} 0 ${rem(20)};

  /* Torn-paper edge towards the (white) benefits bar above. */
  &:before {
    display: block;
    content: '';
    width: 100%;
    position: absolute;
    left: 0;
    top: ${rem(-1)};
    height: ${rem(11)};
    background: top url('/images/torn-border.svg') repeat-x;
  }
`;

const Inner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 ${rem(35)};
  display: flex;
  align-items: flex-start;
  gap: ${rem(40)};

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    gap: ${rem(16)};
  `};

  ${({ theme }) => theme.mediab.s450`
    padding: 0 ${rem(16)};
  `};
`;

const TextCol = styled.div`
  flex: 0 0 40%;

  ${({ theme }) => theme.mediab.m760`
    flex: 0 0 auto;
  `};
`;

const Title = styled.p`
  font-size: ${rem(18)};
  font-weight: 700;
  margin: 0 0 ${rem(6)};
`;

const Description = styled.p`
  font-size: ${rem(14)};
  line-height: 1.5;
  margin: 0;
`;

const FormCol = styled.div`
  flex: 1 1 auto;
  width: 100%;
`;

const FormEl = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(12)};
  align-items: flex-start;

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    align-items: stretch;
    gap: ${rem(10)};
  `};
`;

const InputsRow = styled.div`
  flex: 1 1 auto;
  display: flex;
  gap: ${rem(12)};
  align-items: flex-start;

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    gap: ${rem(10)};
  `};
`;

const InputWrap = styled.div`
  flex: 1 1 50%;
  width: 100%;
`;

const NewsletterInput = styled(InputStyled)`
  width: 100%;
  background: ${({ theme }) => theme.color.white};
`;

const SubmitButton = styled(ButtonStyled)`
  flex: 0 0 auto;
  padding: ${rem(11)} ${rem(28)};

  ${({ theme }) => theme.mediab.m760`
    order: 3;
    align-self: center;
    padding: ${rem(11)} ${rem(40)};
  `};
`;

const ConsentRow = styled.div`
  flex-basis: 100%;
  font-size: ${rem(14)};

  ${({ theme }) => theme.mediab.m760`
    order: 2;
    flex-basis: auto;
  `};

  a {
    color: ${({ theme }) => theme.color.text};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const Message = styled.p`
  margin: 0 0 ${rem(8)};
  font-weight: 700;

  &.success {
    color: ${({ theme }) => theme.color.success};
  }

  &.error {
    color: ${({ theme }) => theme.color.error};
  }
`;

interface Props {
  settings?: {
    newsletterCouponActive?: string | null;
  };
}

interface State {
  submitting: boolean;
  success: boolean;
  errorMsg: string | null;
  alreadyApplied: boolean;
}

class FooterNewsletter extends React.PureComponent<Props, State> {
  public state: State = {
    submitting: false,
    success: false,
    errorMsg: null,
    alreadyApplied: false,
  };

  public componentDidMount() {
    if (cookie.load(UPSELL_NEWSLETTER_APPLIED_COOKIE)) {
      this.setState({ alreadyApplied: true });
    }
  }

  public onSubmit = async (values: {
    name: string;
    email: string;
    marketting_accept?: string;
  }) => {
    this.setState({ submitting: true, errorMsg: null });
    try {
      const customerCardVerifyResponse: any = await API.customerCardVerify(
        {},
        { ean: false, email: true, value: values.email },
      );
      const isSchoolCompanyEmailExistsResponse = await API.isEmailExist({
        email: values.email,
        isSchoolOrCompany: true,
      });

      if (customerCardVerifyResponse?.user) {
        this.setState({
          submitting: false,
          errorMsg: __(
            'Zákazník s týmto emailom už u nás získal zľavu 20% na prvý nákup',
          ),
        });
      } else if (isSchoolCompanyEmailExistsResponse) {
        this.setState({
          submitting: false,
          errorMsg: __('Účet s takýmto emailom už u nás existuje'),
        });
      } else {
        await API.upsellNewsletterApply(
          {},
          { name: values.name, email: values.email },
        );
        cookie.save(UPSELL_NEWSLETTER_APPLIED_COOKIE, 1, { path: '/' });
        this.setState({ submitting: false, success: true });
      }
    } catch (e: any) {
      this.setState({
        submitting: false,
        errorMsg:
          e?.details?.status === 409
            ? __(
                'Zákazník s týmto emailom už u nás získal zľavu 20% na prvý nákup',
              )
            : __('Pri odosielaní nastala chyba, skúste to prosím neskôr.'),
      });
    }
  };

  public render() {
    const { submitting, success, errorMsg, alreadyApplied } = this.state;

    // Rovnaké nastavenie ako pri upsell modale - ak je kupón vypnutý v admine,
    // formulár sa nesmie zobraziť ani v pätičke.
    if (!this.props.settings?.newsletterCouponActive) {
      return null;
    }

    if (alreadyApplied && !success) {
      return null;
    }

    return (
      <Wrapper>
        <Inner>
          <TextCol>
            <Title>{__('Akcie, zľavy a novinky na váš e-mail')}</Title>
            <Description>
              {__(
                'Zľava 20 % pre nových zákazníkov. Stačí sa prihlásiť na odber noviniek a zľava je vaša. Zľava neplatí na nákup nábytku a vybavenia škôl.',
              )}
            </Description>
          </TextCol>

          <FormCol>
            {success ? (
              <Message className="success">
                {__('Boli ste úspešne prihlásený do newslettra')}
              </Message>
            ) : (
              <Loader loading={submitting}>
                {errorMsg ? (
                  <Message className="error">{errorMsg}</Message>
                ) : null}
                <Form
                  onSubmit={this.onSubmit}
                  // tslint:disable-next-line:jsx-no-lambda
                  render={({ handleSubmit }) => (
                    <FormEl onSubmit={handleSubmit}>
                      <InputsRow>
                        <Field name="name" validate={required}>
                          {({ input, meta }) => (
                            <InputWrap>
                              <NewsletterInput
                                {...input}
                                placeholder={__('Vaše meno')}
                                error={meta.error && meta.touched}
                              />
                              {meta.error && meta.touched && (
                                <ErrorPopup>{meta.error}</ErrorPopup>
                              )}
                            </InputWrap>
                          )}
                        </Field>
                        <Field
                          name="email"
                          validate={composeValidators(required, emailValidator)}
                        >
                          {({ input, meta }) => (
                            <InputWrap>
                              <NewsletterInput
                                {...input}
                                placeholder={__('Váš email')}
                                error={meta.error && meta.touched}
                              />
                              {meta.error && meta.touched && (
                                <ErrorPopup>{meta.error}</ErrorPopup>
                              )}
                            </InputWrap>
                          )}
                        </Field>
                      </InputsRow>
                      <SubmitButton
                        type="submit"
                        className="primary"
                        disabled={submitting}
                      >
                        {__('Prihlásiť')}
                      </SubmitButton>
                      <Field
                        name="marketting_accept"
                        type="checkbox"
                        validate={required}
                      >
                        {({ input, meta }) => (
                          <ConsentRow>
                            <CheckboxStyled
                              {...input}
                              value="1"
                              id="footer_marketting_accept"
                            >
                              <span>
                                {__(
                                  'Súhlasím so zasielaním obchodných oznámení a ',
                                )}
                                <a href="/gdpr" target="_blank">
                                  {__('spracovaním osobných údajov')}
                                </a>
                                {'.'}
                              </span>
                            </CheckboxStyled>
                            {meta.error && meta.touched && (
                              <ErrorPopup>{meta.error}</ErrorPopup>
                            )}
                          </ConsentRow>
                        )}
                      </Field>
                    </FormEl>
                  )}
                />
              </Loader>
            )}
          </FormCol>
        </Inner>
      </Wrapper>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    settings: state.general.settings,
  };
};

export default connect(mapStateToProps)(FooterNewsletter);
